/**
 * InsightsDetailDialog Component
 *
 * Shows full details of an InsightsAnalysis document.
 * Sections: Summary, Aggregated Insights (with source CRUD), Identification Matrix,
 * Discriminative Questions, Affinity Rules.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Tabs,
  Tab,
  Chip,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Collapse,
  Button,
  Link,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  TextField,
  Slider,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  Replay as ReplayIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { getProviderForModel } from '@modules/ui-components';
import { useProjectDefaultAIModel } from '@modules/ai-settings';
import { AgentApiClient } from '@modules/content-agent';
import type { InsightsAnalysisDoc, InsightsAudienceTaskContext } from '../services/InsightsFirestoreService';
import { InsightsFirestoreService } from '../services/InsightsFirestoreService';
import { ActorSourcePopover } from './ActorSourcePopover';
import { AddSourceDialog } from './AddSourceDialog';
import { TrustChip } from './TrustChip';
import {
  removeSourceFromCluster,
  addSourceToCluster,
  updateInsightInCategory,
} from '../utils/clusterUtils';
import {
  averageAudienceScore,
  formatAudienceLabel,
  formatReadiness,
  getB2BAudienceMetrics,
} from '../utils/b2bAudienceMetrics';
import { toDate } from '../utils/toDate';
import { InsightVariablesTab } from './InsightVariablesTab';

interface InsightsDetailDialogProps {
  open: boolean;
  analysis: InsightsAnalysisDoc | null;
  onClose: () => void;
  userId?: string;
  onTaskStarted?: (taskId: string, description: string) => void;
  /** R6 deep-link: open directly on a tab (currently only 'variables'). */
  initialTab?: 'variables';
  /** R6 deep-link: preselect this generation in the Variables tab. */
  initialGenerationId?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  coreJobs: 'Core Jobs',
  contexts: 'Contexts',
  fears: 'Fears',
  bigJobs: 'Big Jobs',
  subtasks: 'Subtasks',
  competitors: 'Competitors',
  habits: 'Habits',
};

const JTBD_CATEGORY_KEYS = Object.keys(CATEGORY_LABELS);

interface TaskScopeEntry {
  scopeId: string;
  taskId?: string;
  snapshotKey?: string;
  taskName: string;
  actorCount?: number;
  audienceIds: string[];
  count: number;
  hasPackage: boolean;
}

interface DerivedTaskScope {
  taskId?: string;
  taskName?: string;
  snapshotKey?: string;
  audienceIds: string[];
  actorCount?: number;
}

type InsightsResumeMode = 'retry_failed' | 'continue' | 'recompute_all' | 'packaging_tasks';

interface CategoryRegenerationControls {
  instruction: string;
  mergeSimilarity: number;
}

interface ScopedCategoryQuestionSet {
  id?: string;
  scope?: string;
  taskId?: string;
  category: string;
  summary?: string;
  questionText?: string;
  options?: Array<{
    insightId: string;
    label?: string;
    canonicalDescription?: string;
    actorCount?: number;
    cumulativeWeight?: number;
  }>;
}

const MERGE_BOUNDARY_TIERS = [
  {
    value: 0,
    min: 0,
    max: 24,
    label: 'Roughly similar',
    range: '0-25',
    shortLabel: 'Rough',
    description: 'Approximate same JTBD problem, intent, or outcome.',
  },
  {
    value: 25,
    min: 25,
    max: 49,
    label: 'Significantly similar',
    range: '25-50',
    shortLabel: 'Significant',
    description: 'Same main JTBD intent and outcome; some context variation can remain.',
  },
  {
    value: 50,
    min: 50,
    max: 74,
    label: 'Very similar',
    range: '50-75',
    shortLabel: 'Very',
    description: 'Same core thesis, outcome, and main mechanism.',
  },
  {
    value: 75,
    min: 75,
    max: 94,
    label: 'Nearly identical',
    range: '75-95',
    shortLabel: 'Near-id',
    description: 'Almost interchangeable onboarding thesis.',
  },
  {
    value: 95,
    min: 95,
    max: 100,
    label: 'Identical',
    range: '95-100',
    shortLabel: 'Identical',
    description: 'Only true duplicates or paraphrases with the same meaning.',
  },
] as const;

function getMergeBoundaryTier(value: number) {
  const normalized = Math.max(0, Math.min(100, Math.round(value)));
  return MERGE_BOUNDARY_TIERS.find((tier) => normalized >= tier.min && normalized <= tier.max)
    ?? MERGE_BOUNDARY_TIERS[MERGE_BOUNDARY_TIERS.length - 1];
}

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return value === index
    ? (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          pt: 2,
          pr: 0.5,
        }}
      >
        {children}
      </Box>
    )
    : null;
}

function asArray(value: unknown): any[] {
  return Array.isArray(value) ? value : [];
}

function formatPercent(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value) ? `${(value * 100).toFixed(0)}%` : '—';
}

function formatScore(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value)}/100` : '—';
}

function labelForAudience(audienceId: string, actorRefs: any[]): string {
  const ref = actorRefs.find((item: any) => item.audienceId === audienceId && item.audienceName);
  return ref?.audienceName ? `${ref.audienceName} (${audienceId})` : audienceId;
}

function normalizeCategoryKeys(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(
    value
      .map((item) => String(item || '').trim())
      .filter((item) => JTBD_CATEGORY_KEYS.includes(item)),
  ));
}

function getIncompleteCategoryKeys(analysis: InsightsAnalysisDoc | null): string[] {
  const failed = normalizeCategoryKeys(analysis?.failedCategories);
  const result = new Set(failed);
  const categoryStatus = analysis?.categoryStatus;
  if (categoryStatus && typeof categoryStatus === 'object') {
    for (const category of JTBD_CATEGORY_KEYS) {
      if ((categoryStatus as Record<string, unknown>)[category] !== 'completed') {
        result.add(category);
      }
    }
  }
  return Array.from(result);
}

function countInsightsByCategory(insights: Record<string, any[]>): number {
  return Object.values(insights).reduce((sum: number, items: any) => (
    sum + (Array.isArray(items) ? items.length : 0)
  ), 0);
}

function taskCategoryCountsFromPackages(packages: Record<string, any>): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const category of JTBD_CATEGORY_KEYS) counts[category] = 0;
  for (const packageDoc of Object.values(packages)) {
    for (const category of JTBD_CATEGORY_KEYS) {
      const insights = packageDoc?.aggregatedInsights?.[category];
      const questionSet = packageDoc?.categoryQuestionSets?.[category];
      if (Array.isArray(insights) && insights.length > 0 && questionSet) {
        counts[category] += insights.length;
      }
    }
  }
  return counts;
}

function insightsFromCategoryPackages(packages: Record<string, any>): Record<string, any[]> {
  const insights: Record<string, any[]> = {};
  for (const category of JTBD_CATEGORY_KEYS) {
    const packageDoc = packages[category];
    const categoryInsights = packageDoc?.aggregatedInsights?.[category];
    insights[category] = Array.isArray(categoryInsights) ? categoryInsights : [];
  }
  return insights;
}

function questionSetsFromCategoryPackages(packages: Record<string, any>): Record<string, ScopedCategoryQuestionSet> {
  const questionSets: Record<string, ScopedCategoryQuestionSet> = {};
  for (const category of JTBD_CATEGORY_KEYS) {
    const packageDoc = packages[category];
    const questionSet = packageDoc?.categoryQuestionSet;
    if (questionSet) questionSets[category] = { ...questionSet, category };
  }
  return questionSets;
}

function normalizeQuestionSets(value: unknown): Record<string, ScopedCategoryQuestionSet> {
  if (!value || typeof value !== 'object') return {};
  const out: Record<string, ScopedCategoryQuestionSet> = {};
  for (const [category, questionSet] of Object.entries(value as Record<string, any>)) {
    if (!questionSet || typeof questionSet !== 'object') continue;
    out[category] = { ...questionSet, category: questionSet.category || category };
  }
  return out;
}

function orderedQuestionSets(value: Record<string, ScopedCategoryQuestionSet>): ScopedCategoryQuestionSet[] {
  return JTBD_CATEGORY_KEYS
    .map((category) => value[category])
    .filter((questionSet): questionSet is ScopedCategoryQuestionSet => Boolean(questionSet));
}

function formatScopeName(value: string, fallback: string): string {
  const clean = String(value || '').trim();
  if (!clean) return fallback;
  return clean.length > 18 ? `${clean.slice(0, 8)}...${clean.slice(-6)}` : clean;
}

function formatTaskScopeName(taskId: string | undefined, explicitName: unknown, fetchedNames: Record<string, string>): string {
  const name = typeof explicitName === 'string' ? explicitName.trim() : '';
  if (name) return name;
  if (taskId && fetchedNames[taskId]) return fetchedNames[taskId];
  return taskId ? formatScopeName(taskId, 'Task') : 'Task';
}

function firstString(values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

function uniqueStrings(values: unknown[]): string[] {
  return Array.from(new Set(values.map((value) => String(value || '').trim()).filter(Boolean)));
}

function deriveTaskScopeFromSnapshotGroup(
  group: any,
  audienceTaskContexts: Record<string, InsightsAudienceTaskContext>,
): DerivedTaskScope | null {
  const actorRefs = asArray(group?.actorRefs);
  const audienceIds = uniqueStrings([
    ...asArray(group?.audienceIds),
    ...actorRefs.map((ref: any) => ref?.audienceId),
  ]);
  const context = audienceIds
    .map((audienceId) => audienceTaskContexts[audienceId])
    .find((item) => item?.taskId);
  const taskId = firstString([
    group?.taskId,
    ...actorRefs.map((ref: any) => ref?.taskId),
    context?.taskId,
  ]);
  const taskName = firstString([
    group?.taskName,
    ...actorRefs.map((ref: any) => ref?.taskName),
    context?.taskName,
  ]);
  const hasPendingAudienceContext = !taskId && audienceIds.some((audienceId) => !audienceTaskContexts[audienceId]);
  if (hasPendingAudienceContext) return null;
  const snapshotKey = typeof group?.snapshotKey === 'string' && group.snapshotKey.trim()
    ? group.snapshotKey.trim()
    : undefined;
  if (!taskId && !snapshotKey) return null;
  const actorCount = typeof group?.actorCount === 'number'
    ? group.actorCount
    : actorRefs.length || undefined;
  return { taskId, taskName, snapshotKey, audienceIds, actorCount };
}

function dedupeActorRefs(actorRefs: any[]): any[] {
  const byKey = new Map<string, any>();
  for (const ref of actorRefs) {
    const actorId = typeof ref?.actorId === 'string' && ref.actorId.trim()
      ? ref.actorId.trim()
      : typeof ref?.id === 'string' && ref.id.trim()
      ? ref.id.trim()
      : '';
    if (!actorId) continue;
    byKey.set(actorId, {
      ...byKey.get(actorId),
      ...ref,
      actorId,
      actorName: ref?.actorName || ref?.name || byKey.get(actorId)?.actorName || actorId,
    });
  }
  return Array.from(byKey.values());
}

function actorRefsFromSnapshotGroup(group: any): any[] {
  const explicitRefs = asArray(group?.actorRefs);
  if (explicitRefs.length > 0) return dedupeActorRefs(explicitRefs);

  return dedupeActorRefs(asArray(group?.actors).map((actor: any) => ({
    actorId: actor?.actorId || actor?.id,
    actorName: actor?.actorName || actor?.name,
    audienceId: actor?.audienceId || group?.audienceId,
    audienceName: actor?.audienceName || group?.audienceName,
    taskId: actor?.taskId || group?.taskId,
    taskName: actor?.taskName || group?.taskName,
  })));
}

function actorRefsFromPackageInsights(packageInsights: Record<string, any[]>, taskId?: string, taskName?: string): any[] {
  const refs: any[] = [];
  for (const insights of Object.values(packageInsights || {})) {
    for (const insight of asArray(insights)) {
      for (const source of asArray(insight?.sources)) {
        refs.push({
          actorId: source?.actorId,
          actorName: source?.actorName,
          audienceId: source?.audienceId,
          audienceName: source?.audienceName,
          taskId: source?.taskId || taskId,
          taskName: source?.taskName || taskName,
        });
      }
    }
  }
  return dedupeActorRefs(refs);
}

function actorRefsForTaskScope(
  activeTaskScope: TaskScopeEntry | undefined,
  snapshotGroups: any[],
  audienceTaskContexts: Record<string, InsightsAudienceTaskContext>,
  activeTaskPkg: any,
): any[] {
  if (!activeTaskScope) return [];

  const refsFromSnapshots: any[] = [];
  for (const group of snapshotGroups) {
    const derived = deriveTaskScopeFromSnapshotGroup(group, audienceTaskContexts);
    const scopeMatches = (
      (activeTaskScope.taskId && derived?.taskId === activeTaskScope.taskId)
      || (activeTaskScope.snapshotKey && derived?.snapshotKey === activeTaskScope.snapshotKey)
      || (activeTaskScope.scopeId && (derived?.taskId === activeTaskScope.scopeId || derived?.snapshotKey === activeTaskScope.scopeId))
    );
    if (scopeMatches) refsFromSnapshots.push(...actorRefsFromSnapshotGroup(group));
  }

  const refs = dedupeActorRefs(refsFromSnapshots);
  if (refs.length > 0) return refs;

  return actorRefsFromPackageInsights(
    activeTaskPkg?.aggregatedInsights || {},
    activeTaskScope.taskId,
    activeTaskPkg?.taskName || activeTaskScope.taskName,
  );
}

function SmallValueList({ items, empty = '—' }: { items: unknown; empty?: string }) {
  const values = asArray(items).map((item) => String(item)).filter(Boolean);
  if (values.length === 0) return <Typography variant="body2" color="text.secondary">{empty}</Typography>;
  return (
    <Stack spacing={0.5}>
      {values.map((item, index) => (
        <Typography key={`${item}-${index}`} variant="body2">• {item}</Typography>
      ))}
    </Stack>
  );
}

// ── Insight Row with expandable sources ──────────────────────────────

interface InsightRowProps {
  item: any;
  category: string;
  projectId: string;
  actorRefs: any[];
  onRemoveSource: (insightId: string, originalItemId: string) => void;
  onAddSources: (insightId: string, category: string) => void;
  /** Per-task scope is read-only (source CRUD stays on the global "All tasks" view). */
  readOnly?: boolean;
}

function InsightRow({ item, category, projectId, actorRefs, onRemoveSource, onAddSources, readOnly = false }: InsightRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverSource, setPopoverSource] = useState<any>(null);

  const sources = item.sources || [];

  return (
    <>
      <TableRow
        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell sx={{ maxWidth: 300, wordBreak: 'break-word' }}>
          <Box display="flex" alignItems="center" gap={0.5}>
            {expanded ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
            {item.canonicalDescription}
          </Box>
        </TableCell>
        <TableCell align="center">
          <Chip label={item.cumulativeWeight} size="small" color={item.cumulativeWeight >= 10 ? 'primary' : 'default'} />
        </TableCell>
        <TableCell align="center">{item.frequency}</TableCell>
      </TableRow>

      {/* Expanded sources */}
      <TableRow>
        <TableCell colSpan={3} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1, pl: 4 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  Sources ({sources.length}):
                </Typography>
                {!readOnly && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={(e) => { e.stopPropagation(); onAddSources(item.id, category); }}
                  >
                    Add
                  </Button>
                )}
              </Box>

              {sources.map((source: any) => (
                <Box
                  key={source.originalItemId}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    py: 0.75,
                    px: 1,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {/* Actor name — clickable */}
                  <Link
                    component="button"
                    variant="body2"
                    fontWeight={600}
                    sx={{ minWidth: 150, textAlign: 'left', flexShrink: 0 }}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      setPopoverAnchor(e.currentTarget);
                      setPopoverSource(source);
                    }}
                  >
                    {source.actorName}
                  </Link>

                  {/* Original description */}
                  <Typography variant="body2" sx={{ flex: 1, color: 'text.secondary' }}>
                    &ldquo;{source.originalDescription}&rdquo;
                  </Typography>

                  {/* Weight + similarity */}
                  <Stack direction="row" spacing={0.5} flexShrink={0}>
                    {source.role && <Chip label={source.role} size="small" variant="outlined" />}
                    {source.forceTag && <Chip label={source.forceTag} size="small" color="info" variant="outlined" />}
                    {source.pyramidLevel && <Chip label={source.pyramidLevel} size="small" color="secondary" variant="outlined" />}
                    <Chip label={`w:${source.originalWeight}`} size="small" />
                    {source.similarityScore != null && (
                      <Chip label={`${(source.similarityScore * 100).toFixed(0)}%`} size="small" variant="outlined" />
                    )}
                  </Stack>

                  {/* Delete button */}
                  {!readOnly && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => { e.stopPropagation(); onRemoveSource(item.id, source.originalItemId); }}
                      sx={{ flexShrink: 0 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Actor popover */}
      <ActorSourcePopover
        anchorEl={popoverAnchor}
        open={Boolean(popoverAnchor) && Boolean(popoverSource)}
        onClose={() => { setPopoverAnchor(null); setPopoverSource(null); }}
        source={popoverSource || { actorId: '', actorName: '', audienceId: '', originalItemId: '', originalDescription: '', originalWeight: 0 }}
        category={category}
        projectId={projectId}
      />
    </>
  );
}

function B2BAudienceTab({
  analysis,
  actorRefs,
}: {
  analysis: InsightsAnalysisDoc;
  actorRefs: any[];
}) {
  const mechanics = asArray(analysis.audienceBuyingMechanics);
  const briefs = asArray(analysis.audienceContentBriefs);
  const readinessProfiles = asArray(analysis.audienceReadinessProfiles);
  const audienceIds = Array.from(new Set([
    ...mechanics.map((item: any) => item.audienceId),
    ...briefs.map((item: any) => item.audienceId),
    ...readinessProfiles.map((item: any) => item.audienceId),
  ].filter(Boolean)));

  if (audienceIds.length === 0) {
    return <Typography color="text.secondary">No B2B audience layer found on this analysis.</Typography>;
  }

  const metrics = getB2BAudienceMetrics(analysis);
  const metricByAudience = new Map(metrics.map((metric) => [metric.audienceId, metric]));

  return (
    <Stack spacing={2} data-testid="insights-b2b-audience-tab">
      {audienceIds.map((audienceId) => {
        const mechanic = mechanics.find((item: any) => item.audienceId === audienceId);
        const brief = briefs.find((item: any) => item.audienceId === audienceId);
        const readiness = readinessProfiles.find((item: any) => item.audienceId === audienceId);
        const metric = metricByAudience.get(audienceId);
        const forces = mechanic?.forces ?? {};
        const pyramidCoverage = mechanic?.pyramidCoverage ?? {};
        return (
          <Accordion key={audienceId} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1">{metric ? formatAudienceLabel(metric) : labelForAudience(audienceId, actorRefs)}</Typography>
                {metric?.score != null && <Chip label={`score: ${formatScore(metric.score)}`} size="small" color="success" />}
                {metric?.primaryReaderRole && <Chip label={`reader: ${metric.primaryReaderRole}`} size="small" />}
                {metric && <Chip label={formatReadiness(metric)} size="small" color="primary" variant="outlined" />}
                {readiness?.warmingNeed && <Chip label={`warming: ${readiness.warmingNeed}`} size="small" color="warning" variant="outlined" />}
                {readiness?.buyingSequenceHint && <Chip label={`sequence: ${readiness.buyingSequenceHint}`} size="small" color="secondary" variant="outlined" />}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Box flex={1}>
                    <Typography variant="subtitle2" gutterBottom>Reader Contract</Typography>
                    <Stack spacing={1}>
                      {metric?.score != null && <Chip label={`Audience score: ${formatScore(metric.score)}`} size="small" color="success" sx={{ alignSelf: 'flex-start' }} />}
                      <Typography variant="body2"><strong>Reader:</strong> {brief?.readerDescription || '—'}</Typography>
                      <Typography variant="body2"><strong>Who:</strong> {brief?.who?.readerDescription || brief?.who?.primaryReaderPosition || '—'}</Typography>
                      <Typography variant="body2"><strong>Content job:</strong> {brief?.contentJob || '—'}</Typography>
                      <Typography variant="body2"><strong>Core narrative:</strong> {brief?.coreNarrative || '—'}</Typography>
                      <Typography variant="body2"><strong>Promise:</strong> {brief?.messageFrame?.promise || '—'}</Typography>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Role guidance</Typography>
                        <SmallValueList
                          items={asArray(brief?.roleGuidance).map((item: any) =>
                            `${item.role ?? 'role'}${item.priority ? ` #${item.priority}` : ''}: ${item.emphasis ?? ''}`,
                          )}
                        />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Secondary readers</Typography>
                        <SmallValueList
                          items={asArray(brief?.secondaryReaders).map((item: any) =>
                            `${item.role ?? 'role'}${item.position ? ` (${item.position})` : ''}${item.objectionToPreempt ? ` — ${item.objectionToPreempt}` : ''}`,
                          )}
                        />
                      </Box>
                      <Box><Typography variant="caption" color="text.secondary">Main barriers</Typography><SmallValueList items={brief?.mainBarriers} /></Box>
                      <Box><Typography variant="caption" color="text.secondary">Proof needs</Typography><SmallValueList items={brief?.proofNeeds} /></Box>
                      <Box><Typography variant="caption" color="text.secondary">Proof required</Typography><SmallValueList items={brief?.messageFrame?.proofRequired} /></Box>
                      <Box><Typography variant="caption" color="text.secondary">Objections to address</Typography><SmallValueList items={brief?.messageFrame?.objectionToAddress} /></Box>
                      <Box><Typography variant="caption" color="text.secondary">Gatekeeper risks</Typography><SmallValueList items={brief?.gatekeeperRisks} /></Box>
                      <Box><Typography variant="caption" color="text.secondary">Recommended topics</Typography><SmallValueList items={brief?.recommendedTopics} /></Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Editorial angles</Typography>
                        <SmallValueList
                          items={asArray(brief?.editorialAngles).map((item: any) =>
                            `${item.angle ?? ''}${item.formatHint ? ` (${item.formatHint})` : ''}${item.buyingSequencePosition ? ` — ${item.buyingSequencePosition}` : ''}`,
                          )}
                        />
                      </Box>
                      <Box><Typography variant="caption" color="text.secondary">Theme guidance</Typography><SmallValueList items={brief?.themeGuidance} /></Box>
                      <Box><Typography variant="caption" color="text.secondary">Do not say</Typography><SmallValueList items={brief?.doNotSay} /></Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Source trace</Typography>
                        <SmallValueList
                          items={[
                            brief?.sourceTrace?.audienceBuyingMechanicId ? `mechanic: ${brief.sourceTrace.audienceBuyingMechanicId}` : '',
                            ...(brief?.sourceTrace?.sourceActorIds ?? []).slice(0, 4).map((id: string) => `actor: ${id}`),
                            ...(brief?.sourceTrace?.sourceInsightIds ?? []).slice(0, 4).map((id: string) => `insight: ${id}`),
                          ]}
                        />
                      </Box>
                    </Stack>
                  </Box>

                  <Box flex={1}>
                    <Typography variant="subtitle2" gutterBottom>Readiness & Strategy Distance</Typography>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {readiness?.readinessStage && <Chip label={`stage: ${readiness.readinessStage}`} size="small" color="primary" />}
                        {readiness?.stage && <Chip label={`distance: ${readiness.stage}`} size="small" color="primary" variant="outlined" />}
                        {readiness?.temperature && <Chip label={`temp: ${readiness.temperature}`} size="small" color="secondary" variant="outlined" />}
                        {metric?.score != null && <Chip label={`score: ${formatScore(metric.score)}`} size="small" color="success" />}
                        {typeof readiness?.confidence === 'number' && <Chip label={`confidence: ${formatPercent(readiness.confidence)}`} size="small" variant="outlined" />}
                        {readiness?.warmingNeed && <Chip label={`warming: ${readiness.warmingNeed}`} size="small" color="warning" />}
                        {readiness?.buyingSequenceHint && <Chip label={`sequence: ${readiness.buyingSequenceHint}`} size="small" color="secondary" />}
                      </Stack>
                      <Typography variant="body2"><strong>Rationale:</strong> {readiness?.rationale || '—'}</Typography>
                      <Typography variant="body2"><strong>Evidence:</strong> {readiness?.evidence || '—'}</Typography>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Drivers</Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                          {Object.entries(readiness?.drivers ?? {}).map(([key, value]) => (
                            <Chip key={key} label={`${key}: ${value}`} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Blockers</Typography>
                        <SmallValueList
                          items={asArray(readiness?.blockers).map((item: any) =>
                            `${item.role ?? 'role'}: ${item.blocker ?? ''}${item.severity ? ` (${item.severity})` : ''}`,
                          )}
                        />
                      </Box>
                      <Typography variant="body2"><strong>Recommended objective:</strong> {readiness?.recommendedStrategy?.primaryObjective || '—'}</Typography>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Recommended content mix</Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                          {Object.entries(readiness?.recommendedContentMix ?? {}).map(([key, value]) => (
                            <Chip key={key} label={`${key}: ${formatPercent(value)}`} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      </Box>
                      <Divider />
                      <Typography variant="subtitle2">Pyramid Coverage</Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {['product', 'core', 'role', 'image', 'emotional'].map((level) => (
                          <Chip key={level} label={`${level}: ${formatPercent(pyramidCoverage[level])}`} size="small" variant="outlined" />
                        ))}
                      </Stack>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Gatekeeper veto risks from mechanic</Typography>
                        <SmallValueList
                          items={asArray(mechanic?.gatekeeperVetoRisks).map((risk: any) =>
                            `${risk.role ?? 'gatekeeper'}: ${risk.risk ?? ''}${risk.severity ? ` (${risk.severity})` : ''}`,
                          )}
                        />
                      </Box>
                    </Stack>
                  </Box>
                </Stack>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" gutterBottom>Buying Mechanic</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {mechanic?.oneSentenceMechanic || '—'}
                  </Typography>
                  {mechanic?.forceBalance && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Force balance: {String(mechanic.forceBalance)}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>Forces</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Force</TableCell>
                        <TableCell align="right">Intensity</TableCell>
                        <TableCell>Narrative</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {['push', 'pull', 'anxiety', 'habit'].map((force) => (
                        <TableRow key={force}>
                          <TableCell><Chip label={force} size="small" variant="outlined" /></TableCell>
                          <TableCell align="right">{forces[force]?.intensity ?? '—'}</TableCell>
                          <TableCell>{forces[force]?.narrative || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
}

// ── Main Dialog ──────────────────────────────────────────────────────

export const InsightsDetailDialog: React.FC<InsightsDetailDialogProps> = ({
  open,
  analysis,
  onClose,
  userId = '',
  onTaskStarted,
  initialTab,
  initialGenerationId,
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  // Per-task scope (D39): header dropdown switches the Insights view between the global
  // aggregation ('__all__') and one task's per-task package. Default = global (unchanged view).
  const [taskScope, setTaskScope] = useState<string>('__all__');
  const [categoryRegeneration, setCategoryRegeneration] = useState<Record<string, CategoryRegenerationControls>>({});
  const [snapshotTaskGroups, setSnapshotTaskGroups] = useState<any[]>([]);
  const [perTaskPackageDocs, setPerTaskPackageDocs] = useState<Record<string, any>>({});
  const [categoryPackageDocs, setCategoryPackageDocs] = useState<Record<string, any>>({});
  const [taskNamesById, setTaskNamesById] = useState<Record<string, string>>({});
  const [audienceTaskContexts, setAudienceTaskContexts] = useState<Record<string, InsightsAudienceTaskContext>>({});
  const [snapshotGroupsLoading, setSnapshotGroupsLoading] = useState(false);
  const [snapshotGroupsError, setSnapshotGroupsError] = useState<string | null>(null);

  // Local state for editable data
  const [localInsights, setLocalInsights] = useState<Record<string, any[]> | null>(null);
  const [localActorRefs, setLocalActorRefs] = useState<any[] | null>(null);

  // AddSourceDialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogTarget, setAddDialogTarget] = useState<{ insightId: string; category: string } | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [resumeMode, setResumeMode] = useState<InsightsResumeMode | null>(null);

  // Derived values (local edits take priority, safe when analysis is null)
  const globalCategoryPackageInsights = useMemo(
    () => insightsFromCategoryPackages(categoryPackageDocs),
    [categoryPackageDocs],
  );
  const globalCategoryPackageQuestionSets = useMemo(
    () => questionSetsFromCategoryPackages(categoryPackageDocs),
    [categoryPackageDocs],
  );
  const insights = localInsights || globalCategoryPackageInsights;
  const snapshotInputContract = analysis?.inputContract ?? {};
  const parentSnapshotGroups = asArray(snapshotInputContract.taskGroups);
  const snapshotGroups = parentSnapshotGroups.length > 0
    ? parentSnapshotGroups
    : snapshotTaskGroups.length > 0
    ? snapshotTaskGroups
    : [];
  const snapshotCollectionName = typeof snapshotInputContract.snapshotCollection === 'string' && snapshotInputContract.snapshotCollection.trim()
    ? snapshotInputContract.snapshotCollection.trim()
    : 'input_task_snapshots';
  const perTaskPackageCollectionName = typeof analysis?.perTaskPackageCollection === 'string' && analysis.perTaskPackageCollection.trim()
    ? analysis.perTaskPackageCollection.trim()
    : 'per_task_packages';
  const categoryPackageCollectionName = typeof analysis?.categoryPackageCollection === 'string' && analysis.categoryPackageCollection.trim()
    ? analysis.categoryPackageCollection.trim()
    : 'category_packages';
  // Per-task tier (D35/D39): the header dropdown options + the scoped insight set for the Insights tab.
  const perTaskPackages: Record<string, any> = {
    ...(analysis?.perTaskPackages || {}),
    ...perTaskPackageDocs,
  };
  const allTaskCategoryCounts = taskCategoryCountsFromPackages(perTaskPackages);
  const taskScopeEntries = useMemo<TaskScopeEntry[]>(() => {
    const entries = new Map<string, TaskScopeEntry>();

    for (const [taskId, p] of Object.entries(perTaskPackages)) {
      const count = countInsightsByCategory(p?.aggregatedInsights || {}) || Number(p?.insightCount || 0);
      entries.set(taskId, {
        scopeId: taskId,
        taskId,
        taskName: p?.taskName || taskNamesById[taskId] || formatScopeName(taskId, 'Task'),
        actorCount: p?.actorCount,
        audienceIds: [],
        count,
        hasPackage: true,
      });
    }

    for (const group of snapshotGroups) {
      const derived = deriveTaskScopeFromSnapshotGroup(group, audienceTaskContexts);
      if (!derived) continue;
      const { taskId, taskName, snapshotKey, audienceIds, actorCount } = derived;
      const scopeId = taskId || snapshotKey;
      if (!scopeId) continue;

      const existing = entries.get(scopeId);
      const base: TaskScopeEntry = existing || {
        scopeId,
        taskId,
        snapshotKey,
        taskName: taskId
          ? formatTaskScopeName(taskId, taskName, taskNamesById)
          : formatScopeName(String(group?.audienceName || audienceIds[0] || snapshotKey), 'Audience'),
        actorCount,
        audienceIds,
        count: 0,
        hasPackage: false,
      };
      const mergedActorCount = base.hasPackage
        ? base.actorCount
        : existing
        ? (base.actorCount ?? 0) + (actorCount ?? 0)
        : actorCount;

      entries.set(scopeId, {
        ...base,
        taskId: base.taskId || taskId,
        snapshotKey: base.snapshotKey || snapshotKey,
        taskName: base.taskId || taskId
          ? formatTaskScopeName(base.taskId || taskId, taskName || base.taskName, taskNamesById)
          : base.taskName,
        actorCount: mergedActorCount,
        audienceIds: uniqueStrings([...base.audienceIds, ...audienceIds]),
        count: base.hasPackage ? base.count : 0,
      });
    }

    return Array.from(entries.values());
  }, [audienceTaskContexts, perTaskPackages, snapshotGroups, taskNamesById]);
  const isTaskScope = taskScope !== '__all__';
  const activeTaskScope = isTaskScope ? taskScopeEntries.find((entry) => entry.scopeId === taskScope) : undefined;
  const taskPackageTaskIds = uniqueStrings(taskScopeEntries.map((entry) => entry.taskId));
  const missingTaskPackageIds = taskScopeEntries
    .filter((entry) => entry.taskId && !entry.hasPackage)
    .map((entry) => entry.taskId!) as string[];
  const uniqueMissingTaskPackageIds = uniqueStrings(missingTaskPackageIds);
  const activeTaskPkg = activeTaskScope?.taskId ? perTaskPackages[activeTaskScope.taskId] : null;
  const packageInsights = activeTaskPkg?.aggregatedInsights || {};
  const scopedInsights: Record<string, any[]> = isTaskScope ? packageInsights : insights;
  const scopedInsightCount = countInsightsByCategory(scopedInsights);
  const globalActorRefs = localActorRefs || analysis?.selectedActorRefs || [];
  const actorRefs = isTaskScope
    ? actorRefsForTaskScope(activeTaskScope, snapshotGroups, audienceTaskContexts, activeTaskPkg)
    : globalActorRefs;
  const scopedSummary = isTaskScope ? activeTaskPkg?.summary || '' : analysis?.summary || '';
  const scopedQuestionSets = isTaskScope
    ? normalizeQuestionSets(activeTaskPkg?.categoryQuestionSets)
    : globalCategoryPackageQuestionSets;
  const categoryQuestions = orderedQuestionSets(scopedQuestionSets);
  const deterministicQuestions = isTaskScope ? asArray(activeTaskPkg?.discriminativeQuestions) : analysis?.discriminativeQuestions || [];
  const rules = isTaskScope ? asArray(activeTaskPkg?.affinityRules) : analysis?.affinityRules || [];
  const matrix = isTaskScope ? activeTaskPkg?.identificationMatrix : analysis?.identificationMatrix;
  const scopedAggregationControls = isTaskScope ? activeTaskPkg?.aggregationControls : analysis?.aggregationControls;
  const projectId = analysis?.projectId || '';
  const analysisId = analysis?.id || '';
  const projectDefaultModel = useProjectDefaultAIModel(projectId || null);
  const audienceBuyingMechanics = asArray(analysis?.audienceBuyingMechanics);
  const audienceContentBriefs = asArray(analysis?.audienceContentBriefs);
  const audienceReadinessProfiles = asArray(analysis?.audienceReadinessProfiles);
  const b2bAudienceMetrics = getB2BAudienceMetrics(analysis);
  const averageB2BScore = averageAudienceScore(b2bAudienceMetrics);
  const hasB2BAudienceLayer = analysis?.mode === 'b2b'
    || audienceBuyingMechanics.length > 0
    || audienceContentBriefs.length > 0
    || audienceReadinessProfiles.length > 0;
  const b2bTabIndex = hasB2BAudienceLayer ? 1 : -1;
  const insightsTabIndex = hasB2BAudienceLayer ? 2 : 1;
  const identificationTabIndex = hasB2BAudienceLayer ? 3 : 2;
  const questionsTabIndex = hasB2BAudienceLayer ? 4 : 3;
  const affinityTabIndex = hasB2BAudienceLayer ? 5 : 4;
  const variablesTabIndex = hasB2BAudienceLayer ? 6 : 5;
  const maxTabIndex = variablesTabIndex;
  const distributionMode = matrix?.priorDistributionMode === 'audience' ? 'audience' : 'actor';
  const distributionLabel = distributionMode === 'audience' ? 'Audience' : 'Actor';
  const hasFrozenSnapshot = snapshotInputContract.snapshotStorage === 'task_subcollection';
  const incompleteCategoryKeys = getIncompleteCategoryKeys(analysis);
  const continueCategoryKeys = incompleteCategoryKeys.length > 0 || analysis?.status === 'completed'
    ? incompleteCategoryKeys
    : JTBD_CATEGORY_KEYS;
  const canResumeInsights = Boolean(projectId && analysisId && userId && hasFrozenSnapshot);
  const showRecoveryPanel = hasFrozenSnapshot
    || Boolean(analysis?.status && analysis.status !== 'completed')
    || incompleteCategoryKeys.length > 0;

  useEffect(() => {
    if (tabIndex > maxTabIndex) setTabIndex(0);
  }, [tabIndex, maxTabIndex]);

  // R6 deep-link: land on the Variables tab when asked to.
  useEffect(() => {
    if (open && initialTab === 'variables') setTabIndex(variablesTabIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTab]);

  useEffect(() => {
    setSnapshotTaskGroups([]);
    setPerTaskPackageDocs({});
    setCategoryPackageDocs({});
    setTaskNamesById({});
    setAudienceTaskContexts({});
    setSnapshotGroupsError(null);
    setSnapshotGroupsLoading(false);
    setTaskScope('__all__');
    setCategoryRegeneration({});
  }, [analysisId]);

  useEffect(() => {
    if (!open || !projectId || !analysisId) return;
    if (analysis?.categoryPackageStorage !== 'subcollection' && !analysis?.categoryPackageCollection) return;

    let active = true;
    const service = new InsightsFirestoreService(projectId);
    service.listCategoryPackages(analysisId, categoryPackageCollectionName)
      .then((packages) => {
        if (!active) return;
        setCategoryPackageDocs(Object.fromEntries(packages.map((pkg) => [
          pkg.category || pkg.id,
          { ...pkg, category: pkg.category || pkg.id },
        ])));
      })
      .catch((error: any) => {
        console.error('InsightsDetailDialog.listCategoryPackages', error);
        if (active) setCategoryPackageDocs({});
      });

    return () => {
      active = false;
    };
  }, [analysis?.categoryPackageCollection, analysis?.categoryPackageStorage, analysisId, categoryPackageCollectionName, open, projectId]);

  useEffect(() => {
    if (!open || !projectId || !analysisId) return;
    if (analysis?.perTaskPackageStorage !== 'subcollection' && !analysis?.perTaskPackageCollection) return;

    let active = true;
    const service = new InsightsFirestoreService(projectId);
    service.listPerTaskPackages(analysisId, perTaskPackageCollectionName)
      .then((packages) => {
        if (!active) return;
        setPerTaskPackageDocs(Object.fromEntries(packages.map((pkg) => [
          pkg.taskId || pkg.id,
          { ...pkg, taskId: pkg.taskId || pkg.id },
        ])));
      })
      .catch((error: any) => {
        console.error('InsightsDetailDialog.listPerTaskPackages', error);
        if (active) setPerTaskPackageDocs({});
      });

    return () => {
      active = false;
    };
  }, [analysis?.perTaskPackageCollection, analysis?.perTaskPackageStorage, analysisId, open, perTaskPackageCollectionName, projectId]);

  useEffect(() => {
    if (!open || !projectId || !analysisId) return;
    if (snapshotInputContract.snapshotStorage !== 'task_subcollection') return;
    if (parentSnapshotGroups.length > 0) return;

    let active = true;
    setSnapshotGroupsLoading(true);
    setSnapshotGroupsError(null);
    const service = new InsightsFirestoreService(projectId);
    service.listInputTaskSnapshots(analysisId, snapshotCollectionName)
      .then((groups) => {
        if (!active) return;
        setSnapshotTaskGroups(groups.map((group) => ({
          ...group,
          snapshotKey: group.snapshotKey || group.id,
        })));
      })
      .catch((error: any) => {
        if (!active) return;
        setSnapshotGroupsError(error?.message || 'Failed to load task snapshots');
      })
      .finally(() => {
        if (active) setSnapshotGroupsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    analysisId,
    open,
    parentSnapshotGroups.length,
    projectId,
    snapshotCollectionName,
    snapshotInputContract.snapshotStorage,
  ]);

  useEffect(() => {
    if (!projectId || snapshotGroups.length === 0) return;
    const audienceIds = uniqueStrings(snapshotGroups.flatMap((group: any) => [
      ...asArray(group?.audienceIds),
      ...asArray(group?.actorRefs).map((ref: any) => ref?.audienceId),
    ]));
    const missingAudienceIds = audienceIds.filter((audienceId) => !audienceTaskContexts[audienceId]);
    if (missingAudienceIds.length === 0) return;

    let active = true;
    const service = new InsightsFirestoreService(projectId);
    service.getAudienceTaskContexts(missingAudienceIds)
      .then((contexts) => {
        if (!active || Object.keys(contexts).length === 0) return;
        setAudienceTaskContexts((current) => ({ ...current, ...contexts }));
      })
      .catch(() => {
        // Non-blocking: the dropdown can still fall back to snapshot task ids or compact ids.
      });

    return () => {
      active = false;
    };
  }, [audienceTaskContexts, projectId, snapshotGroups]);

  useEffect(() => {
    if (taskScope === '__all__') return;
    if (!taskScopeEntries.some((entry) => entry.scopeId === taskScope)) {
      setTaskScope('__all__');
    }
  }, [taskScope, taskScopeEntries]);

  useEffect(() => {
    if (!projectId || taskScopeEntries.length === 0) return;
    const missingTaskIds = taskScopeEntries
      .map((entry) => entry.taskId)
      .filter((taskId): taskId is string => Boolean(taskId && !taskNamesById[taskId]));
    if (missingTaskIds.length === 0) return;

    let active = true;
    const service = new InsightsFirestoreService(projectId);
    service.getTaskNames(missingTaskIds)
      .then((names) => {
        if (!active || Object.keys(names).length === 0) return;
        setTaskNamesById((current) => ({ ...current, ...names }));
      })
      .catch(() => {
        // Non-blocking: the dropdown can still fall back to compact ids.
      });

    return () => {
      active = false;
    };
  }, [projectId, taskNamesById, taskScopeEntries]);

  // ── Save partial updates to Firestore ──────────────────────────────

  const saveAnalysis = useCallback(async (updates: Record<string, any>) => {
    if (!analysisId || !projectId) return;
    // Optimistic UI update
    if (updates.aggregatedInsights) setLocalInsights(updates.aggregatedInsights);
    if (updates.selectedActorRefs) setLocalActorRefs(updates.selectedActorRefs);
    try {
      const service = new InsightsFirestoreService(projectId);
      await service.update(analysisId, updates);
      setSnackbar({ open: true, message: 'Saved', severity: 'success' });
    } catch (err) {
      console.error('Save failed:', err);
      setSnackbar({ open: true, message: 'Save failed', severity: 'error' });
    }
  }, [analysisId, projectId]);

  // ── Remove source ──────────────────────────────────────────────────

  const handleRemoveSource = useCallback((insightId: string, originalItemId: string) => {
    for (const [cat, items] of Object.entries(insights)) {
      const insight = (items as any[]).find((i: any) => i.id === insightId);
      if (insight) {
        const updated = removeSourceFromCluster(insight, originalItemId);
        const newInsights = updateInsightInCategory(insights, cat, insightId, updated);
        saveAnalysis({ aggregatedInsights: newInsights });
        return;
      }
    }
  }, [insights, saveAnalysis]);

  // ── Add sources (with actorRefs sync) ──────────────────────────────

  const handleOpenAddDialog = useCallback((insightId: string, category: string) => {
    setAddDialogTarget({ insightId, category });
    setAddDialogOpen(true);
  }, []);

  const handleAddSources = useCallback((newSources: any[]) => {
    if (!addDialogTarget) return;
    const { insightId, category } = addDialogTarget;
    const categoryItems = insights[category] || [];
    const insight = categoryItems.find((i: any) => i.id === insightId);
    if (!insight) return;

    // Add sources to cluster
    let updated = insight;
    for (const source of newSources) {
      updated = addSourceToCluster(updated, source);
    }
    const newInsights = updateInsightInCategory(insights, category, insightId, updated);

    // Sync selectedActorRefs — add new actors not yet tracked
    const currentActorIds = new Set(actorRefs.map((r: any) => r.actorId));
    const newRefs = [...actorRefs];
    for (const source of newSources) {
      if (!currentActorIds.has(source.actorId)) {
        newRefs.push({
          actorId: source.actorId,
          actorName: source.actorName,
          audienceId: source.audienceId,
          audienceName: source.audienceName || '',
        });
        currentActorIds.add(source.actorId);
      }
    }

    // Save both in one Firestore write
    saveAnalysis({
      aggregatedInsights: newInsights,
      selectedActorRefs: newRefs,
    });
  }, [addDialogTarget, insights, actorRefs, saveAnalysis]);

  const handleResumeInsights = useCallback(async (
    mode: InsightsResumeMode,
    categories?: string[],
    taskIds?: string[],
    controls?: CategoryRegenerationControls,
  ) => {
    if (!projectId || !analysisId || !userId || resumeMode) return;
    setResumeMode(mode);
    try {
      const model = projectDefaultModel.value;
      const client = new AgentApiClient();
      const response = await client.resumeInsights({
        projectId,
        userId,
        analysisId,
        mode,
        categories,
        taskIds,
        aggregationInstruction: controls?.instruction,
        mergeSimilarity: controls?.mergeSimilarity,
        provider: getProviderForModel(model),
        model,
        maxTokens: 100000,
      });

      if (!response.success) {
        setSnackbar({
          open: true,
          message: response.error || 'Failed to start insights recovery',
          severity: 'error',
        });
        return;
      }

      if (response.taskId) {
        const scope = mode === 'packaging_tasks'
          ? `${response.taskIds?.length ? `per-task packages: ${response.taskIds.length}` : 'per-task packages'}${response.categories?.length ? ` (${response.categories.map((category) => CATEGORY_LABELS[category] || category).join(', ')})` : ''}`
          : response.categories?.length
          ? response.categories.map((category) => CATEGORY_LABELS[category] || category).join(', ')
          : 'selected phases';
        onTaskStarted?.(response.taskId, `Insights recovery: ${scope}`);
      }
      setSnackbar({ open: true, message: 'Insights recovery task started', severity: 'success' });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to start insights recovery',
        severity: 'error',
      });
    } finally {
      setResumeMode(null);
    }
  }, [analysisId, projectDefaultModel.value, projectId, resumeMode, onTaskStarted, userId]);

  const updateCategoryRegeneration = useCallback((
    category: string,
    patch: Partial<CategoryRegenerationControls>,
  ) => {
    setCategoryRegeneration((current) => ({
      ...current,
      [category]: {
        instruction: current[category]?.instruction ?? '',
        mergeSimilarity: current[category]?.mergeSimilarity ?? 70,
        ...patch,
      },
    }));
  }, []);

  const getCategoryRegeneration = useCallback((category: string): CategoryRegenerationControls => (
    categoryRegeneration[category] ?? { instruction: '', mergeSimilarity: 70 }
  ), [categoryRegeneration]);

  const handleRegenerateCategory = useCallback((category: string) => {
    const controls = getCategoryRegeneration(category);
    if (isTaskScope) {
      if (!activeTaskScope?.taskId) return;
      handleResumeInsights('packaging_tasks', [category], [activeTaskScope.taskId], controls);
      return;
    }
    handleResumeInsights('recompute_all', [category], undefined, controls);
  }, [
    activeTaskScope?.taskId,
    getCategoryRegeneration,
    handleResumeInsights,
    isTaskScope,
  ]);

  const canRegenerateCurrentScope = canResumeInsights
    && !resumeMode
    && (!isTaskScope || Boolean(activeTaskScope?.taskId));

  // Get current sources for AddSourceDialog
  const addDialogCurrentSources = addDialogTarget
    ? ((insights[addDialogTarget.category] || []).find((i: any) => i.id === addDialogTarget.insightId)?.sources || [])
    : [];

  // Early return AFTER all hooks
  if (!analysis) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          display: 'flex',
          height: 'min(760px, 86dvh)',
          minHeight: 'min(560px, 86dvh)',
          overflow: 'hidden',
        },
      }}
    >
      {/* iOS nav bar layout: close LEFT, title CENTER, task-scope dropdown RIGHT (or phantom). */}
      <DialogTitle
        sx={{
          display: 'grid',
          gridTemplateColumns: '40px 1fr auto',
          alignItems: 'center',
          flexShrink: 0,
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="close dialog"
            sx={{ backgroundColor: (theme) => theme.palette.action.hover }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {analysis.name || 'Insights Analysis'}
        </Typography>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ minWidth: 0 }}
        >
          {/* Spec D3: analysis-level trust chip + lineage supersession note. */}
          <TrustChip evidence={(analysis as Record<string, unknown>).evidence as never} />
          {typeof (analysis as Record<string, unknown>).supersededBy === 'string' && (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label={`superseded by ${String((analysis as Record<string, unknown>).supersededBy).slice(0, 8)}…`}
              data-testid="insights-superseded-chip"
            />
          )}
          {taskScopeEntries.length > 0 ? (
            <Select
              size="small"
              value={taskScope}
              onChange={(e) => setTaskScope(String(e.target.value))}
              data-testid="insights-task-scope-select"
              aria-label="Insights task scope"
              sx={{ minWidth: 130, maxWidth: 210, '& .MuiSelect-select': { py: 0.75 } }}
              renderValue={(val) => (val === '__all__'
                ? 'All tasks'
                : (taskScopeEntries.find((t) => t.scopeId === val)?.taskName || 'Task'))}
            >
              <MenuItem value="__all__">
                All tasks (global) ({countInsightsByCategory(insights)})
              </MenuItem>
              {taskScopeEntries.map((t) => (
                <MenuItem key={t.scopeId} value={t.scopeId} data-testid={`insights-task-scope-${t.scopeId}`}>
                  {t.taskName} ({t.count})
                </MenuItem>
              ))}
            </Select>
          ) : snapshotGroupsLoading ? (
            <Chip label="Loading tasks" size="small" variant="outlined" />
          ) : null}
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ flexShrink: 0 }}
        >
          <Tab label="Summary" />
          {hasB2BAudienceLayer && (
            <Tab
              label={`B2B Audience (${Math.max(audienceContentBriefs.length, audienceBuyingMechanics.length, audienceReadinessProfiles.length)})${averageB2BScore != null ? ` · ${averageB2BScore}/100` : ''}`}
              data-testid="insights-tab-b2b-audience"
            />
          )}
          <Tab label={`Insights (${scopedInsightCount})`} />
          <Tab label="Identification" />
          <Tab label={`Questions (${categoryQuestions.length})`} />
          <Tab label={`Affinity (${rules.length})`} />
          <Tab label="Variables" />
        </Tabs>

        {/* Summary */}
        <TabPanel value={tabIndex} index={0}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {analysis.mode && <Chip label={`mode: ${analysis.mode}`} size="small" color={analysis.mode === 'b2b' ? 'primary' : 'default'} />}
            {isTaskScope && (
              <Chip label={activeTaskPkg?.taskName || activeTaskScope?.taskName || 'Task'} size="small" color="secondary" />
            )}
            <Chip label={`insights: ${scopedInsightCount}`} size="small" variant="outlined" />
            <Chip label={`questions: ${categoryQuestions.length}`} size="small" variant="outlined" />
            <Chip label={`affinity: ${rules.length}`} size="small" variant="outlined" />
            {isTaskScope && actorRefs.length > 0 && (
              <Chip label={`actors: ${actorRefs.length}`} size="small" variant="outlined" />
            )}
            {hasB2BAudienceLayer && (
              <>
                {averageB2BScore != null && <Chip label={`audience score: ${averageB2BScore}/100`} size="small" color="success" />}
                <Chip label={`buying mechanics: ${audienceBuyingMechanics.length}`} size="small" variant="outlined" />
                <Chip label={`content briefs: ${audienceContentBriefs.length}`} size="small" variant="outlined" />
                <Chip label={`readiness profiles: ${audienceReadinessProfiles.length}`} size="small" variant="outlined" />
              </>
            )}
          </Stack>
          {showRecoveryPanel && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                border: 1,
                borderColor: hasFrozenSnapshot ? 'divider' : 'warning.main',
                borderRadius: 1,
              }}
            >
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="subtitle2">Recovery</Typography>
                  <Chip
                    label={hasFrozenSnapshot ? 'frozen input snapshot' : 'no frozen snapshot'}
                    size="small"
                    color={hasFrozenSnapshot ? 'success' : 'warning'}
                    variant="outlined"
                  />
                  {snapshotInputContract.snapshotStorage && (
                    <Chip label={`storage: ${snapshotInputContract.snapshotStorage}`} size="small" variant="outlined" />
                  )}
                  {taskScopeEntries.length > 0 && (
                    <Chip label={`tasks: ${taskScopeEntries.length}`} size="small" variant="outlined" />
                  )}
                  {snapshotGroups.length > taskScopeEntries.length && (
                    <Chip label={`snapshots: ${snapshotGroups.length}`} size="small" variant="outlined" />
                  )}
                  {snapshotGroupsLoading && (
                    <Chip label="loading task snapshots" size="small" variant="outlined" />
                  )}
                  {incompleteCategoryKeys.length > 0 && (
                    <Chip label={`retry categories: ${incompleteCategoryKeys.length}`} size="small" color="warning" />
                  )}
                </Stack>
                {snapshotGroupsError && (
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    Task snapshots could not be loaded for the header scope selector: {snapshotGroupsError}
                  </Alert>
                )}
                {incompleteCategoryKeys.length > 0 && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {incompleteCategoryKeys.map((category) => (
                      <Chip key={category} label={CATEGORY_LABELS[category] || category} size="small" variant="outlined" />
                    ))}
                  </Stack>
                )}
                {!hasFrozenSnapshot && (
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    This analysis cannot be resumed because it was created before frozen input snapshots were stored.
                  </Alert>
                )}
                {hasFrozenSnapshot && !userId && (
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    Sign in context is missing, so recovery cannot be started from this dialog.
                  </Alert>
                )}
                {hasFrozenSnapshot && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={resumeMode === 'retry_failed' ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />}
                      disabled={!canResumeInsights || incompleteCategoryKeys.length === 0 || Boolean(resumeMode)}
                      onClick={() => handleResumeInsights('retry_failed', incompleteCategoryKeys)}
                    >
                      Retry failed categories
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={resumeMode === 'continue' ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                      disabled={!canResumeInsights || continueCategoryKeys.length === 0 || Boolean(resumeMode)}
                      onClick={() => handleResumeInsights('continue', continueCategoryKeys)}
                    >
                      Continue from snapshot
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={resumeMode === 'recompute_all' ? <CircularProgress size={16} /> : <ReplayIcon />}
                      disabled={!canResumeInsights || Boolean(resumeMode)}
                      onClick={() => handleResumeInsights('recompute_all')}
                    >
                      Recompute all categories
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={resumeMode === 'packaging_tasks' ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                      disabled={!canResumeInsights || taskPackageTaskIds.length === 0 || Boolean(resumeMode)}
                      onClick={() => handleResumeInsights('packaging_tasks', undefined, uniqueMissingTaskPackageIds.length > 0 ? uniqueMissingTaskPackageIds : taskPackageTaskIds)}
                    >
                      {uniqueMissingTaskPackageIds.length > 0 ? 'Generate missing task packages' : 'Regenerate task packages'}
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Box>
          )}
          {hasB2BAudienceLayer && b2bAudienceMetrics.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Who We Write For</Typography>
              <Stack spacing={1}>
                {b2bAudienceMetrics.map((metric) => (
                  <Box
                    key={metric.audienceId}
                    sx={{
                      p: 1.25,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                      <Typography variant="subtitle2">{formatAudienceLabel(metric)}</Typography>
                      {metric.score != null && <Chip label={`score: ${formatScore(metric.score)}`} size="small" color="success" />}
                      {metric.primaryReaderRole && <Chip label={`reader: ${metric.primaryReaderRole}`} size="small" />}
                      <Chip label={formatReadiness(metric)} size="small" color="primary" variant="outlined" />
                      {metric.buyingSequenceHint && <Chip label={`sequence: ${metric.buyingSequenceHint}`} size="small" color="secondary" variant="outlined" />}
                    </Stack>
                    {metric.readerDescription && (
                      <Typography variant="body2">{metric.readerDescription}</Typography>
                    )}
                    {metric.contentJob && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {metric.contentJob}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                      {metric.mainBarriers.slice(0, 2).map((item) => <Chip key={`barrier-${item}`} label={`barrier: ${item}`} size="small" variant="outlined" />)}
                      {metric.proofNeeds.slice(0, 2).map((item) => <Chip key={`proof-${item}`} label={`proof: ${item}`} size="small" variant="outlined" />)}
                      {metric.gatekeeperRisks.slice(0, 1).map((item) => <Chip key={`risk-${item}`} label={`risk: ${item}`} size="small" color="warning" variant="outlined" />)}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
          <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
            {scopedSummary || 'No summary available.'}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Actors analyzed:</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {actorRefs.map((ref: any) => (
              <Chip key={ref.actorId} label={`${ref.actorName} (${ref.audienceName})`} size="small" variant="outlined" />
            ))}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Status: <Chip label={analysis.status} size="small" sx={{ ml: 0.5 }} />
            {toDate(analysis.createdAt) && ` | Created: ${toDate(analysis.createdAt)!.toLocaleString()}`}
          </Typography>
        </TabPanel>

        {/* Aggregated Insights with source CRUD (scoped by the header Task-scope dropdown — D39) */}
        <TabPanel value={tabIndex} index={insightsTabIndex}>
          {isTaskScope && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">Showing insights for</Typography>
              <Chip label={activeTaskPkg?.taskName || activeTaskScope?.taskName || 'task'} size="small" color="secondary" />
              {(activeTaskPkg?.actorCount ?? activeTaskScope?.actorCount) != null && (
                <Chip label={`${activeTaskPkg?.actorCount ?? activeTaskScope?.actorCount} actors`} size="small" variant="outlined" />
              )}
              <Chip label={`${scopedInsightCount} insights`} size="small" variant="outlined" />
              <Chip label="read-only per-task view" size="small" variant="outlined" />
            </Stack>
          )}
          {isTaskScope && !activeTaskPkg && scopedInsightCount === 0 && (
            <Alert
              severity="info"
              action={activeTaskScope?.taskId ? (
                <Button
                  color="inherit"
                  size="small"
                  disabled={!canResumeInsights || Boolean(resumeMode)}
                  onClick={() => handleResumeInsights('packaging_tasks', undefined, [activeTaskScope.taskId!])}
                >
                  Generate
                </Button>
              ) : undefined}
              sx={{ mb: 1.5 }}
            >
              Per-task insights for this task have not been generated yet.
            </Alert>
          )}
          {scopedInsightCount === 0 && (!isTaskScope || activeTaskPkg) && (
            <Typography variant="body2" color="text.secondary">
              No insights for the current scope.
            </Typography>
          )}
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
            const items = scopedInsights[key] || [];
            if (items.length === 0) return null;
            const regenerationControls = getCategoryRegeneration(key);
            const generatedControls = scopedAggregationControls?.categories?.[key];
            const mergeBoundaryTier = getMergeBoundaryTier(regenerationControls.mergeSimilarity);
            const generatedBoundaryTier = typeof generatedControls?.mergeSimilarity === 'number'
              ? getMergeBoundaryTier(generatedControls.mergeSimilarity)
              : null;
            return (
              <Accordion key={key} defaultExpanded={items.length <= 5}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">{label} ({items.length})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.25}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    sx={{ mb: 1.5 }}
                  >
                    <TextField
                      size="small"
                      value={regenerationControls.instruction}
                      onChange={(event) => updateCategoryRegeneration(key, { instruction: event.target.value })}
                      placeholder="Optional regeneration instruction"
                      inputProps={{ 'aria-label': `${label} regeneration instruction` }}
                      sx={{ flex: 1, minWidth: { md: 280 } }}
                    />
                    <Box sx={{ minWidth: { xs: '100%', md: 360 }, maxWidth: { md: 420 } }}>
                      <Typography variant="caption" color="text.secondary">
                        Merge boundary: {regenerationControls.mergeSimilarity} - {mergeBoundaryTier.label} ({mergeBoundaryTier.range})
                      </Typography>
                      <Slider
                        size="small"
                        min={0}
                        max={100}
                        step={1}
                        marks={MERGE_BOUNDARY_TIERS.map((tier) => ({
                          value: tier.value,
                          label: tier.shortLabel,
                        }))}
                        value={regenerationControls.mergeSimilarity}
                        onChange={(_, value) => updateCategoryRegeneration(key, { mergeSimilarity: Array.isArray(value) ? value[0] : value })}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => getMergeBoundaryTier(Number(value)).label}
                        aria-label={`${label} merge boundary strictness`}
                        sx={{
                          mt: 0.5,
                          '& .MuiSlider-markLabel': {
                            fontSize: 10,
                            color: 'text.secondary',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25, lineHeight: 1.25 }}>
                        {mergeBoundaryTier.description}
                      </Typography>
                      {typeof generatedControls?.mergeSimilarity === 'number' && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
                          Generated with: {generatedControls.mergeSimilarity} - {generatedBoundaryTier?.label} ({generatedBoundaryTier?.range})
                        </Typography>
                      )}
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={resumeMode ? <CircularProgress size={16} /> : <ReplayIcon />}
                      disabled={!canRegenerateCurrentScope}
                      onClick={() => handleRegenerateCategory(key)}
                      data-testid={`insights-regenerate-category-${key}`}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Regenerate
                    </Button>
                  </Stack>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Description</TableCell>
                        <TableCell align="center">Weight</TableCell>
                        <TableCell align="center">Actors</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item: any) => (
                        <InsightRow
                          key={item.id}
                          item={item}
                          category={key}
                          projectId={projectId}
                          actorRefs={actorRefs}
                          onRemoveSource={handleRemoveSource}
                          onAddSources={handleOpenAddDialog}
                          readOnly
                        />
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </TabPanel>

        {/* Identification Matrix */}
        <TabPanel value={tabIndex} index={identificationTabIndex}>
          {matrix?.priorDistribution ? (
            <>
              <Typography variant="subtitle2" gutterBottom>Prior Distribution (before any questions)</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <Chip label={`mode: ${distributionMode}`} size="small" color={distributionMode === 'audience' ? 'primary' : 'default'} />
                {matrix.confidence != null && <Chip label={`confidence: ${formatPercent(matrix.confidence)}`} size="small" variant="outlined" />}
              </Stack>
              <Table size="small" sx={{ mb: 3, maxWidth: 500 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{distributionLabel}</TableCell>
                    <TableCell align="right">Probability</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(matrix.priorDistribution).map(([entityId, prob]: [string, any]) => {
                    const ref = distributionMode === 'audience'
                      ? { label: labelForAudience(entityId, actorRefs) }
                      : { label: actorRefs.find((r: any) => r.actorId === entityId)?.actorName || entityId };
                    return (
                      <TableRow key={entityId}>
                        <TableCell>{ref.label}</TableCell>
                        <TableCell align="right">{(prob * 100).toFixed(1)}%</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {matrix.decisionTree && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Decision Tree (first question)</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Best first question: &quot;{matrix.decisionTree.insightDescription?.slice(0, 100)}&quot;
                    (info gain: {matrix.decisionTree.informationGain?.toFixed(3)} bits)
                  </Typography>
                </>
              )}
            </>
          ) : (
            <Typography color="text.secondary">No identification matrix computed.</Typography>
          )}
        </TabPanel>

        {/* Category Questions */}
        <TabPanel value={tabIndex} index={questionsTabIndex}>
          {categoryQuestions.length > 0 ? (
            <Stack spacing={1.5}>
              {categoryQuestions.map((questionSet) => (
                <Box
                  key={questionSet.id || questionSet.category}
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    <Chip label={CATEGORY_LABELS[questionSet.category] || questionSet.category} size="small" color="primary" variant="outlined" />
                    {questionSet.scope && <Chip label={questionSet.scope} size="small" variant="outlined" />}
                    <Chip label={`${asArray(questionSet.options).length} options`} size="small" variant="outlined" />
                  </Stack>
                  {questionSet.summary && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {questionSet.summary}
                    </Typography>
                  )}
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {questionSet.questionText || `Which ${CATEGORY_LABELS[questionSet.category] || questionSet.category} option best describes you?`}
                  </Typography>
                  <Stack spacing={0.75}>
                    {asArray(questionSet.options).map((option: any) => (
                      <Box
                        key={option.insightId}
                        sx={{
                          p: 1,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                        }}
                      >
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 220 }}>
                            {option.label || option.canonicalDescription || option.insightId}
                          </Typography>
                          {typeof option.actorCount === 'number' && <Chip label={`${option.actorCount} actors`} size="small" />}
                          {typeof option.cumulativeWeight === 'number' && <Chip label={`weight: ${option.cumulativeWeight}`} size="small" variant="outlined" />}
                        </Stack>
                        {option.canonicalDescription && option.canonicalDescription !== option.label && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {option.canonicalDescription}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary">
              No category question sets found for the current scope. Regenerate the category or task package to create them.
            </Typography>
          )}
        </TabPanel>

        {/* Affinity Rules */}
        <TabPanel value={tabIndex} index={affinityTabIndex}>
          {rules.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rule</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell align="right">Lift</TableCell>
                  <TableCell align="right">Confidence</TableCell>
                  <TableCell align="right">Support</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.slice(0, 30).map((rule: any) => (
                  <TableRow key={rule.id}>
                    <TableCell sx={{ maxWidth: 500, wordBreak: 'break-word', fontSize: '0.85rem' }}>
                      {rule.interpretation}
                    </TableCell>
                    <TableCell>
                      {rule.scope ? <Chip label={rule.scope} size="small" variant="outlined" /> : '—'}
                    </TableCell>
                    <TableCell align="right">{rule.lift?.toFixed(1)}</TableCell>
                    <TableCell align="right">{(rule.confidence * 100).toFixed(0)}%</TableCell>
                    <TableCell align="right">{(rule.support * 100).toFixed(0)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography color="text.secondary">No affinity rules found.</Typography>
          )}
        </TabPanel>

        {/* B2B Audience Layer */}
        {hasB2BAudienceLayer && (
          <TabPanel value={tabIndex} index={b2bTabIndex}>
            <B2BAudienceTab analysis={analysis} actorRefs={actorRefs} />
          </TabPanel>
        )}

        {/* Variables */}
        <TabPanel value={tabIndex} index={variablesTabIndex}>
          <InsightVariablesTab
            analysisId={analysisId}
            analysisName={analysis.name || 'Insights Analysis'}
            projectId={projectId}
            initialGenerationId={initialGenerationId}
            aggregatedInsights={scopedInsights}
            categoryQuestionSets={scopedQuestionSets}
            identificationMatrix={matrix}
            discriminativeQuestions={deterministicQuestions}
            affinityRules={rules}
            selectedActorRefs={actorRefs}
            taskScope={isTaskScope ? 'task' : 'global'}
            taskId={activeTaskScope?.taskId}
            taskName={activeTaskPkg?.taskName || activeTaskScope?.taskName}
            hasTaskPackage={!isTaskScope || Boolean(activeTaskPkg)}
            taskTargets={taskScopeEntries
              .filter(entry => Boolean(entry.taskId) && entry.hasPackage)
              .map(entry => ({
                taskId: entry.taskId!,
                taskName: entry.taskName,
                hasPackage: entry.hasPackage,
              }))}
            allTaskCategoryCounts={allTaskCategoryCounts}
            onTaskStarted={onTaskStarted}
          />
        </TabPanel>
      </DialogContent>

      {/* Add Source Dialog */}
      <AddSourceDialog
        open={addDialogOpen}
        onClose={() => { setAddDialogOpen(false); setAddDialogTarget(null); }}
        onAdd={handleAddSources}
        category={addDialogTarget?.category || ''}
        currentSources={addDialogCurrentSources}
        actorRefs={actorRefs}
        projectId={projectId}
        clusterDescription={
          addDialogTarget
            ? ((insights[addDialogTarget.category] || []).find((i: any) => i.id === addDialogTarget.insightId)?.canonicalDescription || '')
            : ''
        }
      />

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={2000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Dialog>
  );
};
